import { Component, forwardRef } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import { ModalService } from 'mbs-ui-kit';
import { HelpService } from 'mbs-ui-shared@help/help.service';
import { BackupFileModelForStepsHelper } from 'wizards/helpers/backup-file-model-helpers/backup-file-model-for-steps-helper';
import { BackupFilePlanFromStepsHelper } from 'wizards/helpers/backup-file-model-helpers/backup-file-plan-from-steps-helper';
import { ScheduleType } from 'wizards/models/schedule-models';
import { WizardStepsService } from 'wizards/services/wizard-steps.service';
import { BackupFileFormModel } from '../../models/backupFile/backup-form-model';
import { WizardsServicesHubService } from '../../services/wizards-services-hub.service';
import { WizardBaseClass } from '../wizard-base.class';

type FileBackupWizardHelpDocumentationLinks =
  | 'welcome'
  | 'whereToBackUp'
  | 'advancedOptions'
  | 'whatToBackUp'
  | 'advancedFilter'
  | 'compressionEncryption'
  | 'fullConsistencyCheck'
  | 'schedule'
  | 'retention'
  | 'prePost'
  | 'notification';

@UntilDestroy()
@Component({
  selector: 'mbs-backup-files-wizard',
  templateUrl: './backup-files-wizard.component.html',
  providers: [
    {
      provide: WizardBaseClass,
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      useExisting: forwardRef(() => BackupFilesWizardComponent)
    }
  ]
})
export class BackupFilesWizardComponent extends WizardBaseClass {
  public fromGettingStarted = false;
  public helpDocumentationLinks: Record<FileBackupWizardHelpDocumentationLinks, string>;

  constructor(
    private modalService: ModalService,
    services: WizardsServicesHubService,
    public i18nPipe: I18NextPipe,
    stepService: WizardStepsService,
    helpService: HelpService
  ) {
    super(services, i18nPipe, stepService, helpService);

    this.fromGettingStarted = services.mainService.fromGettingStarted;
    this.FormModel = new BackupFileFormModel(this.isNBF, this.isUnix);

    this.initSubscribes();
  }

  updateStepData(plan, isDefault = false): void {
    this.setLoadedPlanAndRunChainRequestsForBackup(plan);

    if (isDefault) {
      // TODO: MBS-24778 Enable new schedule and retention in Backup Templates
      if (!this.isEdit && this.isNBF && !this.isUnix) {
        this.FormModel.scheduleStep.ScheduleType = 'noschedule';
        this.FormModel.scheduleStep.fullBackupData.enabled = false;
      }

      return this.setDefaultValue();
    }

    const formData = BackupFileModelForStepsHelper.BackupModelFromPlan(plan, this.defaultSettings());

    if (this.isRDMode) formData.advancedOptionsFlags = this.FormModel.advancedOptionsFlags;

    this.FormModel = formData;

    // TODO: MBS-24778 Enable new schedule and retention in Backup Templates
    const rightVersionForSchedule = this.mainService.backupVersionUpdated && +this.mainService.backupVersionUpdated.substring(0, 2) >= 80;
    if (!this.isEdit && this.isNBF && !this.isUnix && !rightVersionForSchedule) {
      this.FormModel.scheduleStep.ScheduleType = 'noschedule';
      this.FormModel.scheduleStep.fullBackupData.enabled = false;
    }
  }

  enableVSSHandler(value: boolean): void {
    if (!this.isUnix) {
      this.FormModel.advancedOptionsStep.AlwaysUseVSS = value;
      if (!value) this.FormModel.advancedOptionsStep.UseFastNtfsScan = false;
      this.FormModel.advancedOptionsStep = { ...this.FormModel.advancedOptionsStep };
    }
  }

  offFastNTFSHandler(): void {
    this.FormModel.advancedOptionsStep.UseFastNtfsScan = false;
    this.FormModel.advancedOptionsStep.AlwaysUseVSS = true;
    this.FormModel.advancedOptionsStep = { ...this.FormModel.advancedOptionsStep };
    this.forceNextStepWizard(false, 'advancedOptionsStep', 'Advanced Options');
  }

  handleSave(): void {
    const recTypeNumber = +ScheduleType[this.FormModel.scheduleStep.ScheduleType];

    if (
      !this.isUnix &&
      !this.isNBF &&
      ((recTypeNumber === ScheduleType.predefined && this.FormModel.simpleScheduleStep.blockLevelBackup) ||
        (recTypeNumber === ScheduleType.recurring &&
          this.FormModel.scheduleAdvancedStep.blockLevel &&
          this.FormModel.scheduleAdvancedStep.blockLevel.Enabled))
    ) {
      this.enableVSSHandler(true);
    }

    const newPlan = BackupFilePlanFromStepsHelper.getUpdatedPlan(
      this.loadedPlan.plan,
      this.FormModel,
      this.defaultSettings(),
      this.isNBF,
      this.isUnix
    );

    this.savePlan(newPlan);
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  loadInfoHandler() {}

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected loadInfoHandlerFinally() {}

  protected initHelpDocumentationLinks(): void {
    let osPrefix = ''; // Default for windows and isRDMode

    if (this.mainService.isLinux) {
      osPrefix = 'linux-';
    }
    if (this.mainService.isMac) {
      osPrefix = 'mac-';
    }

    this.helpDocumentationLinks = {
      welcome: `backup/file/${osPrefix}nbf#1`,
      whereToBackUp: `backup/file/${osPrefix}nbf#2`,
      advancedOptions: `backup/file/${osPrefix}nbf#3`,
      whatToBackUp: `backup/file/${osPrefix}nbf#4`,
      advancedFilter: `backup/file/${osPrefix}nbf#5`,
      compressionEncryption: `backup/file/${osPrefix}nbf#6`,
      fullConsistencyCheck: `backup/file/${osPrefix}nbf#7`,
      schedule: `backup/file/${osPrefix}nbf#8`,
      retention: `backup/file/${osPrefix}nbf#9`,
      prePost: `backup/file/${osPrefix}nbf#10`,
      notification: `backup/file/${osPrefix}nbf#11`
    };

    super.initHelpDocumentationLinks();
  }
}
